import { useEffect, useRef, useState } from "react";
import type { TutorialOption } from "@/api/catalogs";

/**
 * Bouton "Comment faire ?" qui ouvre un panneau latéral avec le tutoriel
 * (F-SAIS-019).
 *
 * Forme V1 = guide écrit + GIFs (décision PM, BRD § 3.3). Le contenu
 * Markdown est rendu via un parseur minimal (titres, listes, gras) sans
 * dépendance externe.
 *
 * Accessibilité :
 *  - role="dialog", aria-modal, aria-labelledby
 *  - Fermeture par Escape
 *  - Focus piégé dans le panneau quand ouvert
 *  - Bouton de fermeture explicite + clic sur l'overlay
 */
export function TutorialButton({
  tutorial,
}: {
  tutorial: TutorialOption | undefined;
}) {
  const [open, setOpen] = useState(false);

  if (!tutorial) return null;

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="inline-flex items-center gap-1 rounded border border-blue-200 bg-blue-50 px-2 py-1 text-xs font-medium text-blue-900 hover:bg-blue-100"
        aria-haspopup="dialog"
      >
        <span aria-hidden="true">?</span>
        <span>Comment faire ?</span>
      </button>
      {open && <TutorialPanel tutorial={tutorial} onClose={() => setOpen(false)} />}
    </>
  );
}

function TutorialPanel({
  tutorial,
  onClose,
}: {
  tutorial: TutorialOption;
  onClose: () => void;
}) {
  const closeButtonRef = useRef<HTMLButtonElement>(null);

  // Fermeture par Escape + focus initial sur le bouton de fermeture
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handler);
    closeButtonRef.current?.focus();
    return () => document.removeEventListener("keydown", handler);
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-50 flex justify-end bg-black/40"
      role="dialog"
      aria-modal="true"
      aria-labelledby={`tutorial-title-${tutorial.key}`}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <aside className="h-full w-full max-w-md overflow-y-auto bg-white shadow-xl">
        <header className="sticky top-0 flex items-center justify-between border-b bg-white px-6 py-4">
          <h2
            id={`tutorial-title-${tutorial.key}`}
            className="text-lg font-semibold"
          >
            {tutorial.title}
          </h2>
          <button
            ref={closeButtonRef}
            type="button"
            onClick={onClose}
            aria-label="Fermer le tutoriel"
            className="rounded p-1 hover:bg-gray-100"
          >
            <span aria-hidden="true">✕</span>
          </button>
        </header>

        <div className="space-y-4 px-6 py-4">
          {tutorial.summary && (
            <p className="rounded bg-blue-50 p-3 text-sm text-blue-900">
              {tutorial.summary}
            </p>
          )}

          {tutorial.body_md && (
            <div className="prose-sm space-y-3 text-sm">
              <MarkdownContent source={tutorial.body_md} />
            </div>
          )}

          {tutorial.gifs && tutorial.gifs.length > 0 && (
            <section>
              <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-600">
                Illustrations
              </h3>
              <div className="mt-2 space-y-3">
                {tutorial.gifs.map((g) => (
                  <figure key={g.path}>
                    <img
                      src={g.path}
                      alt={g.caption ?? ""}
                      loading="lazy"
                      className="w-full rounded border border-gray-200"
                      onError={(e) => {
                        // Le GIF n'est pas encore disponible (V1 : placeholder)
                        (e.target as HTMLImageElement).style.display = "none";
                      }}
                    />
                    {g.caption && (
                      <figcaption className="mt-1 text-xs text-gray-600">
                        {g.caption}
                      </figcaption>
                    )}
                  </figure>
                ))}
              </div>
            </section>
          )}

          {tutorial.duration_min && (
            <p className="text-xs text-gray-500">
              Lecture estimée : {tutorial.duration_min} min
            </p>
          )}
        </div>
      </aside>
    </div>
  );
}

/**
 * Mini-renderer Markdown : titres `### `, listes `- `, gras `**...**`,
 * italique `*...*`, code inline `` `...` ``.
 * Suffisant pour les tutoriels rédigés en YAML. Pas de XSS car le contenu
 * vient du backend (config versionnée), pas d'utilisateurs.
 */
function MarkdownContent({ source }: { source: string }) {
  const blocks = parseMarkdownBlocks(source);
  return (
    <>
      {blocks.map((block, i) => {
        switch (block.kind) {
          case "h3":
            return (
              <h3 key={i} className="mt-4 font-semibold">
                {block.text}
              </h3>
            );
          case "ul":
            return (
              <ul key={i} className="list-disc pl-5">
                {block.items.map((item, j) => (
                  <li key={j} dangerouslySetInnerHTML={{ __html: inlineMarkdown(item) }} />
                ))}
              </ul>
            );
          case "p":
            return (
              <p key={i} dangerouslySetInnerHTML={{ __html: inlineMarkdown(block.text) }} />
            );
        }
      })}
    </>
  );
}

type MarkdownBlock =
  | { kind: "p"; text: string }
  | { kind: "h3"; text: string }
  | { kind: "ul"; items: string[] };

function parseMarkdownBlocks(source: string): MarkdownBlock[] {
  const lines = source.split("\n");
  const blocks: MarkdownBlock[] = [];
  let currentList: string[] | null = null;
  let currentP: string[] | null = null;

  const flush = () => {
    if (currentList) {
      blocks.push({ kind: "ul", items: currentList });
      currentList = null;
    }
    if (currentP) {
      const text = currentP.join(" ").trim();
      if (text) blocks.push({ kind: "p", text });
      currentP = null;
    }
  };

  for (const rawLine of lines) {
    const line = rawLine.replace(/\s+$/, "");
    if (line.startsWith("### ")) {
      flush();
      blocks.push({ kind: "h3", text: line.slice(4) });
    } else if (line.startsWith("- ")) {
      flush();
      currentList ??= [];
      currentList.push(line.slice(2));
    } else if (line.trim() === "") {
      flush();
    } else if (currentList) {
      // Continuation d'item liste (indenté)
      const last = currentList[currentList.length - 1];
      currentList[currentList.length - 1] = last + " " + line.trim();
    } else {
      currentP ??= [];
      currentP.push(line.trim());
    }
  }
  flush();
  return blocks;
}

function inlineMarkdown(s: string): string {
  // Échappement HTML basique d'abord
  const escaped = s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
  return escaped
    .replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>")
    .replace(/(?<!\w)\*([^*]+)\*(?!\w)/g, "<em>$1</em>")
    .replace(/`([^`]+)`/g, '<code class="rounded bg-gray-100 px-1 text-xs">$1</code>');
}
